import React, { Suspense, useEffect, useCallback } from "react";
import type { FC, ReactElement } from "react";

import { createBottomTabNavigator } from "@react-navigation/bottom-tabs";
import type { BottomTabNavigationProp } from "@react-navigation/bottom-tabs";
import type { NavigatorScreenParams } from "@react-navigation/native";
import { HStack } from "native-base";
import { usePreloadedQuery, useQueryLoader } from "react-relay";
import type { PreloadedQuery } from "react-relay";

import DevBanner from "pianofunclub-crm/components/General/DevBanner";
import AccountsNavigator from "pianofunclub-crm/navigation/AccountsNavigator";
import type { AccountsStackNavigatorParamList } from "pianofunclub-crm/navigation/AccountsNavigator";
import InvoicingNavigator from "pianofunclub-crm/navigation/InvoicingNavigator";
import type { InvoicingStackNavigatorParamList } from "pianofunclub-crm/navigation/InvoicingNavigator";
import PayrollNavigator from "pianofunclub-crm/navigation/PayrollNavigator";
import type { PayrollNavigatorParamList } from "pianofunclub-crm/navigation/PayrollNavigator";
import ProfileNavigator from "pianofunclub-crm/navigation/ProfileNavigator";
import type { ProfileStackNavigatorParamList } from "pianofunclub-crm/navigation/ProfileNavigator";
import RegistersNavigator from "pianofunclub-crm/navigation/RegistersNavigator";
import type { RegistersStackNavigatorParamList } from "pianofunclub-crm/navigation/RegistersNavigator";
import SchoolsNavigator from "pianofunclub-crm/navigation/SchoolsNavigator";
import type { SchoolsStackNavigatorParamList } from "pianofunclub-crm/navigation/SchoolsNavigator";
import TeacherRegistersNavigator from "pianofunclub-crm/navigation/TeacherRegistersNavigator";
import type { TeacherRegistersStackNavigatorParamList } from "pianofunclub-crm/navigation/TeacherRegistersNavigator";
import TeacherTimetableNavigator from "pianofunclub-crm/navigation/TeacherTimetableNavigator";
import type { TeacherTimetableStackNavigatorParamList } from "pianofunclub-crm/navigation/TeacherTimetableNavigator";
import WaitingListNavigator from "pianofunclub-crm/navigation/WaitingListNavigator";
import type { WaitingListNavigatorParamList } from "pianofunclub-crm/navigation/WaitingListNavigator";
import { getTabIcon } from "pianofunclub-crm/utils/extractors";

import { useAuth } from "pianofunclub-shared/providers/AuthProviderContext";
import type { MeQuery } from "pianofunclub-shared/relay/graphql/general/__generated__/MeQuery.graphql";
import { me } from "pianofunclub-shared/relay/graphql/general/Me";
import LoadingScreen from "pianofunclub-shared/screens/LoadingScreen";
import { customColors } from "pianofunclub-shared/theme";

export type HubNavigatorParamList = {
  AccountsHubTab: NavigatorScreenParams<AccountsStackNavigatorParamList>;
  InvoicingHubTab: NavigatorScreenParams<InvoicingStackNavigatorParamList>;
  PayrollHubTab: NavigatorScreenParams<PayrollNavigatorParamList>;
  ProfileHubTab: NavigatorScreenParams<ProfileStackNavigatorParamList>;
  RegistersHubTab: NavigatorScreenParams<RegistersStackNavigatorParamList>;
  SchoolsHubTab: NavigatorScreenParams<SchoolsStackNavigatorParamList>;
  TeacherRegistersHubTab: NavigatorScreenParams<TeacherRegistersStackNavigatorParamList>;
  TeacherTimetableHubTab: NavigatorScreenParams<TeacherTimetableStackNavigatorParamList>;
  WaitingListHubTab: NavigatorScreenParams<WaitingListNavigatorParamList>;
};

export type HubTabNavigatorProps<T extends keyof HubNavigatorParamList> = BottomTabNavigationProp<
  HubNavigatorParamList,
  T
>;

interface Props {
  meQueryReference: PreloadedQuery<MeQuery, Record<string, unknown>>;
}

const HubTabNavigator = createBottomTabNavigator<HubNavigatorParamList>();

const HubNavigator = (props: Props): ReactElement => {
  const { meQueryReference } = props;

  const { authState, setUser, user } = useAuth();

  const my_user = usePreloadedQuery<MeQuery>(me, meQueryReference);

  useEffect(() => {
    setUser(my_user?.me);
  }, [my_user, setUser]);

  const getNavigators = useCallback(() => {
    switch (authState.accountType) {
      case "STAFF":
        return (
          <>
            <HubTabNavigator.Screen
              component={AccountsNavigator}
              name="AccountsHubTab"
              options={{
                tabBarIconStyle: {
                  flex: 1,
                  minWidth: 100,
                },
                tabBarLabel: "Accounts",
                title: "Accounts",
              }}
            />
            <HubTabNavigator.Screen
              component={RegistersNavigator}
              name="RegistersHubTab"
              options={{
                tabBarLabel: "Registers",
                title: "Registers",
              }}
            />
            <HubTabNavigator.Screen
              component={InvoicingNavigator}
              name="InvoicingHubTab"
              options={{
                tabBarLabel: "Invoicing",
                title: "Invoicing",
              }}
            />
            <HubTabNavigator.Screen
              component={SchoolsNavigator}
              name="SchoolsHubTab"
              options={{
                tabBarLabel: "Schools",
                title: "Schools",
              }}
            />
            <HubTabNavigator.Screen
              component={WaitingListNavigator}
              name="WaitingListHubTab"
              options={{
                tabBarLabel: "Waiting List",
                title: "Waiting List",
              }}
            />
            <HubTabNavigator.Screen
              component={PayrollNavigator}
              name="PayrollHubTab"
              options={{
                tabBarLabel: "Partner Payouts",
                title: "Partner Payouts",
              }}
            />
          </>
        );
      default:
        return (
          <>
            <HubTabNavigator.Screen
              component={TeacherRegistersNavigator}
              name="TeacherRegistersHubTab"
              options={{
                tabBarIconStyle: {
                  flex: 1,
                  marginLeft: 40,
                  minWidth: 60,
                },
                tabBarLabel: "Registers",
                title: "Registers",
              }}
            />
            <HubTabNavigator.Screen
              component={TeacherTimetableNavigator}
              name="TeacherTimetableHubTab"
              options={{
                tabBarLabel: "Timetable",
                title: "Timetable",
              }}
            />
          </>
        );
    }
  }, [authState.accountType]);

  return (
    <HubTabNavigator.Navigator
      initialRouteName={
        user?.profile?.accountType === "TEACHER" ? "TeacherRegistersHubTab" : "AccountsHubTab"
      }
      screenOptions={({ route }) => ({
        cardStyle: { backgroundColor: "#f1f1f1" },
        headerShown: false,
        lazy: false,
        tabBarActiveTintColor: customColors.primary["400"],
        tabBarIcon: ({ color }) => {
          return <HStack space="10">{getTabIcon(route.name, color)}</HStack>;
        },
        tabBarInactiveTintColor: customColors.surface["300"],
        tabBarItemStyle: {
          alignItems: "center",
          flex: 0,
          marginLeft: 50,
        },
        tabBarLabelPosition: "beside-icon",
        tabBarLabelStyle: {
          fontFamily: "Poppins-SemiBold",
          fontSize: 18,
          paddingLeft:
            route.name === "AccountsHubTab" || route.name === "SchoolsHubTab"
              ? 8
              : route.name === "RegistersHubTab"
                ? 1
                : 0,
        },
        tabBarStyle: {
          backgroundColor: customColors.primary["800"],
          borderBottomLeftRadius: 25,
          borderBottomRightRadius: 25,
          borderWidth: 0,
          justifyContent: "flex-start",
          minHeight: 70,
          paddingRight: 50,
          position: "absolute",
          top: 0,
        },
        upperCaseLabel: false,
      })}
    >
      {getNavigators()}
      <HubTabNavigator.Screen
        component={ProfileNavigator}
        name="ProfileHubTab"
        options={{
          tabBarItemStyle: {
            alignItems: "center",
            flex: 0,
            justifyContent: "flex-end",
            marginLeft: "auto",
          },
          tabBarLabel: user?.firstName ?? "Settings",
          tabBarLabelStyle: {
            fontFamily: "Poppins-SemiBold",
            fontSize: 18,
            paddingLeft: 4,
          },
          title: "Settings",
        }}
      />
    </HubTabNavigator.Navigator>
  );
};

const HubNavigatorWrapper: FC = () => {
  const [meQueryReference, loadMeQuery] = useQueryLoader<MeQuery>(me);

  useEffect(() => {
    loadMeQuery({}, { fetchPolicy: "store-and-network" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!meQueryReference) {
    return <LoadingScreen />;
  }

  return (
    <Suspense fallback={<LoadingScreen />}>
      {process.env.REACT_APP_ENV !== "prod" ? <DevBanner /> : null}
      <HubNavigator meQueryReference={meQueryReference} />
    </Suspense>
  );
};

export default HubNavigatorWrapper;
